export interface CreateTeamRequest {
  name: string;
  division?: string;
  gender?: 'men' | 'women' | 'mixed';
  sport?: string;
}

export interface Team {
  id: string;
  name: string;
  division: string | null;
  gender: string | null;
  sport: string;
}

export interface CreateTeamResponse {
  team: Team;
}

export interface UpdateTeamRequest {
  name?: string;
  division?: string | null;
  gender?: 'men' | 'women' | 'mixed' | null;
  sport?: string;
}

export interface TeamDeleteImpact {
  canDelete: boolean;
  message: string;
  details: {
    consentedRowerCount: number;
    linkedSessionCount: number;
  };
}

/**
 * Service for team management API calls
 */
export const teamService = {
  /**
   * Create a new team
   * @param data Team creation data
   * @returns Created team or error
   */
  async createTeam(data: CreateTeamRequest): Promise<{ data?: Team; error?: Error }> {
    try {
      const isLocalhost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      const apiUrl = isLocalhost ? 'http://localhost:3001/api/teams' : '/api/teams';

      // Get auth token from Supabase session
      const { supabase } = await import('../../config/supabase');
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        return { error: new Error('Not authenticated') };
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(data),
      });

      const payload = await response.json().catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[TeamService] Failed to parse JSON response:', err);
        return {};
      });

      if (!response.ok) {
        const errorMsg = payload?.error || `HTTP ${response.status}: ${response.statusText}`;
        return { error: new Error(errorMsg) };
      }

      if (!payload.team) {
        return { error: new Error('Invalid response: team data missing') };
      }

      return { data: payload.team };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[TeamService] Error creating team:', error);
      return {
        error: error instanceof Error ? error : new Error('Unexpected error creating team'),
      };
    }
  },

  /**
   * Update an existing team
   * @param teamId Team ID to update
   * @param data Team update data (only provided fields will be updated)
   * @returns Updated team or error
   */
  async updateTeam(
    teamId: string,
    data: UpdateTeamRequest
  ): Promise<{ data?: Team; error?: Error }> {
    try {
      const isLocalhost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      // Use /api/teams/update as workaround for vercel dev not recognizing [id] routes
      // In production, we can switch to /api/teams/${teamId}
      const apiUrl = isLocalhost ? 'http://localhost:3001/api/teams/update' : '/api/teams/update';

      // eslint-disable-next-line no-console
      console.log('[TeamService] Updating team:', { apiUrl, teamId, data });

      // Get auth token from Supabase session
      const { supabase } = await import('../../config/supabase');
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        // eslint-disable-next-line no-console
        console.error('[TeamService] No session or access token');
        return { error: new Error('Not authenticated') };
      }

      // eslint-disable-next-line no-console
      console.log('[TeamService] Making PATCH request to:', apiUrl);
      // Include teamId in the body for the update endpoint
      const requestBody = { ...data, teamId };
      const response = await fetch(apiUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(requestBody),
      });

      // eslint-disable-next-line no-console
      console.log('[TeamService] Response status:', response.status, response.statusText);

      const payload = await response.json().catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[TeamService] Failed to parse JSON response:', err);
        return {};
      });

      // eslint-disable-next-line no-console
      console.log('[TeamService] Response payload:', payload);

      if (!response.ok) {
        const errorMsg = payload?.error || `HTTP ${response.status}: ${response.statusText}`;
        // eslint-disable-next-line no-console
        console.error('[TeamService] Request failed:', errorMsg);
        return { error: new Error(errorMsg) };
      }

      if (!payload.team) {
        // eslint-disable-next-line no-console
        console.error('[TeamService] No team in response:', payload);
        return { error: new Error('Invalid response: team data missing') };
      }

      // eslint-disable-next-line no-console
      console.log('[TeamService] Team updated successfully:', payload.team);
      return { data: payload.team };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[TeamService] Error updating team:', error);
      return {
        error: error instanceof Error ? error : new Error('Unexpected error updating team'),
      };
    }
  },

  /**
   * Delete a team
   * @param teamId Team ID to delete
   * @param password User's password for verification
   * @returns Success or error
   */
  async deleteTeam(
    teamId: string,
    password: string
  ): Promise<{ success?: boolean; error?: Error }> {
    try {
      const isLocalhost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      const apiUrl = isLocalhost ? 'http://localhost:3001/api/teams/delete' : '/api/teams/delete';

      // eslint-disable-next-line no-console
      console.log('[TeamService] Deleting team:', { apiUrl, teamId });

      // Get auth token from Supabase session
      const { supabase } = await import('../../config/supabase');
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        // eslint-disable-next-line no-console
        console.error('[TeamService] No session or access token');
        return { error: new Error('Not authenticated') };
      }

      // eslint-disable-next-line no-console
      console.log('[TeamService] Making DELETE request to:', apiUrl);
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ teamId, password }),
      });

      // eslint-disable-next-line no-console
      console.log('[TeamService] Response status:', response.status, response.statusText);

      const payload = await response.json().catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[TeamService] Failed to parse JSON response:', err);
        return {};
      });

      // eslint-disable-next-line no-console
      console.log('[TeamService] Response payload:', payload);

      if (!response.ok) {
        const errorMsg = payload?.error || `HTTP ${response.status}: ${response.statusText}`;
        // eslint-disable-next-line no-console
        console.error('[TeamService] Request failed:', errorMsg);
        return { error: new Error(errorMsg) };
      }

      // eslint-disable-next-line no-console
      console.log('[TeamService] Team deleted successfully');
      return { success: true };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[TeamService] Error deleting team:', error);
      return {
        error: error instanceof Error ? error : new Error('Unexpected error deleting team'),
      };
    }
  },

  /**
   * Check whether a team is eligible for deletion.
   * Returns consented-rower/session association counts for UI messaging.
   */
  async getTeamDeleteImpact(teamId: string): Promise<{ data?: TeamDeleteImpact; error?: Error }> {
    try {
      const isLocalhost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      const apiUrl = isLocalhost ? 'http://localhost:3001/api/teams/delete' : '/api/teams/delete';

      const { supabase } = await import('../../config/supabase');
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        return { error: new Error('Not authenticated') };
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ teamId, checkOnly: true }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const errorMsg = payload?.error || `HTTP ${response.status}: ${response.statusText}`;
        return { error: new Error(errorMsg) };
      }

      return { data: payload as TeamDeleteImpact };
    } catch (error) {
      return {
        error:
          error instanceof Error
            ? error
            : new Error('Unexpected error checking team delete impact'),
      };
    }
  },

  /**
   * Send team invitation to a rower
   * @param teamId Team ID
   * @param email Rower's email address
   * @returns Invitation details or error
   */
  async inviteRower(
    teamId: string,
    email: string
  ): Promise<{ data?: { invitation: any }; error?: Error }> {
    try {
      const isLocalhost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      const apiUrl = isLocalhost ? 'http://localhost:3001/api/teams/invite' : '/api/teams/invite';

      const { supabase } = await import('../../config/supabase');
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        return { error: new Error('Not authenticated') };
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ teamId, email }),
      });

      const payload = await response.json().catch((err) => {
        console.error('[TeamService] Failed to parse JSON response:', err);
        return {};
      });

      if (!response.ok) {
        const errorMsg = payload?.error || `HTTP ${response.status}: ${response.statusText}`;
        return { error: new Error(errorMsg) };
      }

      return { data: payload };
    } catch (error) {
      console.error('[TeamService] Error inviting rower:', error);
      return {
        error: error instanceof Error ? error : new Error('Unexpected error inviting rower'),
      };
    }
  },

  /**
   * Get pending invitations for current user
   * @returns List of pending invitations or error
   */
  async getInvitations(): Promise<{ data?: { invitations: any[]; count: number }; error?: Error }> {
    try {
      const isLocalhost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      const apiUrl = isLocalhost
        ? 'http://localhost:3001/api/teams/invitations'
        : '/api/teams/invitations';

      const { supabase } = await import('../../config/supabase');
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        return { error: new Error('Not authenticated') };
      }

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      const payload = await response.json().catch((err) => {
        console.error('[TeamService] Failed to parse JSON response:', err);
        return {};
      });

      if (!response.ok) {
        const errorMsg = payload?.error || `HTTP ${response.status}: ${response.statusText}`;
        return { error: new Error(errorMsg) };
      }

      return { data: payload };
    } catch (error) {
      console.error('[TeamService] Error fetching invitations:', error);
      return {
        error: error instanceof Error ? error : new Error('Unexpected error fetching invitations'),
      };
    }
  },

  /**
   * Accept team invitation
   * @param invitationToken Invitation token
   * @param password User's password for verification
   * @returns Success or error
   */
  async acceptInvitation(
    invitationToken: string,
    password: string
  ): Promise<{ data?: { success: boolean; team: any }; error?: Error }> {
    try {
      const isLocalhost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      const apiUrl = isLocalhost
        ? 'http://localhost:3001/api/teams/accept-invitation'
        : '/api/teams/accept-invitation';

      const { supabase } = await import('../../config/supabase');
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        return { error: new Error('Not authenticated') };
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ invitationToken, password }),
      });

      const payload = await response.json().catch((err) => {
        console.error('[TeamService] Failed to parse JSON response:', err);
        return {};
      });

      if (!response.ok) {
        const errorMsg = payload?.error || `HTTP ${response.status}: ${response.statusText}`;
        return { error: new Error(errorMsg) };
      }

      return { data: payload };
    } catch (error) {
      console.error('[TeamService] Error accepting invitation:', error);
      return {
        error: error instanceof Error ? error : new Error('Unexpected error accepting invitation'),
      };
    }
  },

  /**
   * Decline team invitation
   * @param invitationToken Invitation token
   * @returns Success or error
   */
  async declineInvitation(
    invitationToken: string
  ): Promise<{ data?: { success: boolean; team: any }; error?: Error }> {
    try {
      const isLocalhost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      const apiUrl = isLocalhost
        ? 'http://localhost:3001/api/teams/decline-invitation'
        : '/api/teams/decline-invitation';

      const { supabase } = await import('../../config/supabase');
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        return { error: new Error('Not authenticated') };
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ invitationToken }),
      });

      const payload = await response.json().catch((err) => {
        console.error('[TeamService] Failed to parse JSON response:', err);
        return {};
      });

      if (!response.ok) {
        const errorMsg = payload?.error || `HTTP ${response.status}: ${response.statusText}`;
        return { error: new Error(errorMsg) };
      }

      return { data: payload };
    } catch (error) {
      console.error('[TeamService] Error declining invitation:', error);
      return {
        error: error instanceof Error ? error : new Error('Unexpected error declining invitation'),
      };
    }
  },

  /**
   * Cancel team invitation (coach action)
   * @param invitationId Invitation ID
   * @returns Success or error
   */
  async cancelInvitation(
    invitationId: string
  ): Promise<{ data?: { success: boolean; invitation: any }; error?: Error }> {
    try {
      const isLocalhost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      const apiUrl = isLocalhost
        ? 'http://localhost:3001/api/teams/cancel-invitation'
        : '/api/teams/cancel-invitation';

      const { supabase } = await import('../../config/supabase');
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        return { error: new Error('Not authenticated') };
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ invitationId }),
      });

      const payload = await response.json().catch((err) => {
        console.error('[TeamService] Failed to parse JSON response:', err);
        return {};
      });

      if (!response.ok) {
        const errorMsg = payload?.error || `HTTP ${response.status}: ${response.statusText}`;
        return { error: new Error(errorMsg) };
      }

      return { data: payload };
    } catch (error) {
      console.error('[TeamService] Error cancelling invitation:', error);
      return {
        error: error instanceof Error ? error : new Error('Unexpected error cancelling invitation'),
      };
    }
  },
};
